import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import DepartmentChips from "@/components/DepartmentChips";
import POSendDialog from "@/components/POSendDialog";
import PODetailDialog from "@/components/PODetailDialog";
import AttachProcessorDialog from "@/components/AttachProcessorDialog";
import ClosePODialog from "@/components/ClosePODialog";
import StockThumb from "@/components/StockThumb";
import { fmtQty } from "@/lib/pack";
import {
  FileText,
  Send,
  Sparkles,
  ShoppingCart,
  X,
  Trash2,
  CheckCircle2,
  Plus,
  Minus,
  Wrench,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Eye,
  XCircle,
  Search,
  Printer,
} from "lucide-react";

const STATUS_STYLE = {
  draft: { bg: "#FFF6DF", color: "#9A6F00", label: "Draft" },
  sent: { bg: "#DFEEF7", color: "#1E7CA8", label: "Sent" },
  // A PO reaches "received" via one of two very different paths:
  //   1. Orders manager clicks "PO Actioned" (v176 rename)   → no goods received
  //   2. Receiver ticks off the packing slip on Receive page → real IN movements
  // Managers found the shared green "Received" tile confusing (looked
  // like stock had physically arrived even when only case 1 fired), so
  // we split into two pills below and pick between them at render time
  // based on cumulative qty_received across the PO's lines.
  received: { bg: "#DFF6E6", color: "#107A3E", label: "Delivered" },
  actioned: { bg: "#E6E1F5", color: "#4B2E9B", label: "Actioned" },
  partially_received: { bg: "#FFF3D4", color: "#7A5A00", label: "Part received" },
  // v179 — closed = manually received before the Receive-from-PO loop
  // existed. Hidden from the Receive dashboard, kept in Orders history.
  closed: { bg: "#E4E7EB", color: "#3D4A5C", label: "Closed" },
  cancelled: { bg: "#F0F0F0", color: "#6B6B6B", label: "Cancelled" },
};

// Resolve the pill for a PO — if it's marked received but nothing has
// been ticked off the packing slip yet (qty_received all zero) the
// manager only Actioned it; render the amber "Actioned" pill instead
// of the green "Delivered" one.
const resolveStatusStyle = (po) => {
  if (po.status !== "received") {
    return STATUS_STYLE[po.status] || STATUS_STYLE.draft;
  }
  const anyPhysical = (po.items || []).some(
    (it) => Number(it.qty_received || 0) > 0,
  );
  return anyPhysical ? STATUS_STYLE.received : STATUS_STYLE.actioned;
};

const Orders = () => {
  const { user, isManager } = useAuth();
  const [pos, setPos] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  // Stock-id → departments[] lookup so we can filter POs by department
  // (POs themselves don't carry departments; each line's stock does).
  const [stockDepts, setStockDepts] = useState({});
  // v195 — Department filter for both the Suggested-reorders panel and
  // the PO list. Staff default to their first assigned dept so they
  // don't have to scroll past every other team's low-stock items;
  // managers land on "all". Every user can flip to "all" at any time.
  const [deptFilter, setDeptFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState({});
  const [editedQty, setEditedQty] = useState({});
  const [sendFor, setSendFor] = useState(null);
  const [viewPoId, setViewPoId] = useState(null);
  const [attachProcessorFor, setAttachProcessorFor] = useState(null);
  const [closePo, setClosePo] = useState(null);
  // v181 — PO list filter, search & collapse. The list grows fast — a
  // fresh install had 4 POs after one week — so we ship a chip filter,
  // supplier/number search, and a full-panel collapse toggle so managers
  // can hide history once they've triaged today's inbox.
  const [poStatusFilter, setPoStatusFilter] = useState("active");
  const [poSearch, setPoSearch] = useState("");
  const [poListCollapsed, setPoListCollapsed] = useState(false);
  // v184 — Suggested reorders panel collapse. Auto-hides when there's
  // already an open draft to reduce the "long scroll" pain — managers
  // usually only need one path at a time (add more items, or send).
  const [suggestionsCollapsed, setSuggestionsCollapsed] = useState(false);
  const [suggestionsAutoCollapsed, setSuggestionsAutoCollapsed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creatingForSupplier, setCreatingForSupplier] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  // Per-supplier toll-processing config, keyed by supplier group id.
  // { [sid]: { processor_id: "...", fee_per_log: "" } }
  // Only populated when the manager opens the toll-processing block for
  // the group — untouched groups stay clean and create ordinary POs.
  const [tollByGroup, setTollByGroup] = useState({});
  // Subscription intervals per supplier group (days: 7/14/30/90 or "").
  // Only applied when creating a fresh draft — appended-to-existing-draft
  // path doesn't touch subscription state.
  const [subByGroup, setSubByGroup] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    const [a, b, c, d] = await Promise.all([
      api.get("/po"),
      api.get("/reorder/suggestions"),
      api.get("/suppliers"),
      api.get("/stock"),
    ]);
    setPos(a.data);
    setSuggestions(b.data);
    setSuppliers(c.data);
    // Build stock-id → departments[] map (respects both new
    // `departments[]` and legacy single `department`).
    const map = {};
    (d.data || []).forEach((s) => {
      const arr =
        Array.isArray(s.departments) && s.departments.length
          ? s.departments
          : s.department
            ? [s.department]
            : [];
      map[s.id] = arr;
    });
    setStockDepts(map);
  };
  useEffect(() => {
    load();
  }, []);

  // Default staff to their first assigned department so the low-stock
  // list isn't crowded with other teams' items. Managers stay on "all".
  useEffect(() => {
    if (!user) return;
    if (!isManager && Array.isArray(user.departments) && user.departments.length > 0) {
      setDeptFilter((cur) => (cur === "all" ? user.departments[0] : cur));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // v184 — Auto-collapse the Suggested reorders panel on first load when
  // an open draft already exists — that's the "sticky note" scenario
  // where the manager just wants to reach the draft to Send. We only
  // auto-collapse once so a manual Show doesn't get clobbered.
  useEffect(() => {
    if (suggestionsAutoCollapsed) return;
    if (pos.length === 0) return;
    const hasDraft = pos.some((p) => p.status === "draft" && !p.companion_of);
    if (hasDraft) {
      setSuggestionsCollapsed(true);
    }
    setSuggestionsAutoCollapsed(true);
  }, [pos, suggestionsAutoCollapsed]);

  // Handle ?focus=<stock_id> coming from the Alerts panel — auto-tick, scroll & highlight.
  // Runs when suggestions OR the PO list loads, so we can catch either case:
  //   1. Item is in the suggestions list  → auto-select + scroll to it
  //   2. Item is already on an open draft/sent PO → scroll to that PO
  //      (this is why Vinyl "disappeared" for Marlene — reorder_suggestions
  //      deliberately hides items already on an open PO, but the alert
  //      badge doesn't know that. Without this branch the user lands on
  //      a page that looks empty for their item.)
  useEffect(() => {
    const focusId = searchParams.get("focus");
    if (!focusId) return;
    // Wait until both feeds are in — otherwise we might fire the
    // "already on PO" branch before suggestions land and vice versa.
    if (suggestions.length === 0 && pos.length === 0) return;

    const target = suggestions.find((s) => s.stock_id === focusId);
    if (target) {
      const sid = target.supplier_id || "_unassigned";
      setSelectedIds((s) => ({
        ...s,
        [sid]: { ...(s[sid] || {}), [target.stock_id]: true },
      }));
      setEditedQty((q) =>
        q[target.stock_id] === undefined
          ? { ...q, [target.stock_id]: target.suggested_qty }
          : q,
      );
      // Also expand the supplier group so the row is actually visible
      // — collapsed-by-default meant the highlight fired on an off-screen
      // node and looked like nothing happened.
      setExpandedSuppliers((e) => ({ ...e, [sid]: true }));
      setHighlightId(target.stock_id);
      setTimeout(() => {
        const el = document.querySelector(
          `[data-testid="suggest-${target.stock_id}"]`,
        );
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
      setTimeout(() => setHighlightId(null), 2400);
    } else {
      // Not in suggestions — is it already on an open (draft or sent) PO?
      const openPo = pos.find(
        (p) =>
          (p.status === "draft" || p.status === "sent") &&
          (p.items || []).some((it) => it.stock_id === focusId),
      );
      if (openPo) {
        toast.info(
          `Already on ${openPo.status === "draft" ? "draft" : "sent"} ${openPo.po_number}`,
          {
            description: `Item is on ${openPo.supplier_snapshot?.name || "an open"} PO — jumping to it below.`,
            duration: 6000,
          },
        );
        setHighlightId(openPo.id);
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="po-${openPo.id}"]`);
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
        setTimeout(() => setHighlightId(null), 3000);
      } else {
        toast.info("Item not in current reorder suggestions");
      }
    }
    // Clear the query param either way so it doesn't keep firing on rerenders.
    const next = new URLSearchParams(searchParams);
    next.delete("focus");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions, pos]);

  // v199 — Supplier primary-owner map for the dept filter. When a
  // specific dept is picked, Orders/PO now shows ONLY suppliers whose
  // `primary_department` matches — not any supplier who happens to
  // sell an item tagged to that dept. Managers set the primary owner
  // on the Suppliers page. Stock lookups kept below for empty-state
  // messages only.
  const supplierPrimaryDept = useMemo(() => {
    const m = {};
    (suppliers || []).forEach((s) => {
      m[s.id] = s.primary_department || null;
    });
    return m;
  }, [suppliers]);

  const suggestionsInDept = useMemo(() => {
    if (deptFilter === "all") return suggestions;
    return suggestions.filter(
      (s) => supplierPrimaryDept[s.supplier_id] === deptFilter,
    );
  }, [suggestions, deptFilter, supplierPrimaryDept]);

  const posInDept = useMemo(() => {
    if (deptFilter === "all") return pos;
    return pos.filter(
      (po) => supplierPrimaryDept[po.supplier_id] === deptFilter,
    );
  }, [pos, deptFilter, supplierPrimaryDept]);

  const bySupplier = useMemo(() => {
    const grouped = {};
    suggestionsInDept.forEach((s) => {
      const k = s.supplier_id || "_unassigned";
      grouped[k] = grouped[k] || [];
      grouped[k].push(s);
    });
    // v184 — Alphabetise within each supplier group. Backend sorts by
    // "how far below threshold" for the overall list; managers walking
    // an already-grouped list want to find an item by name.
    Object.keys(grouped).forEach((k) => {
      grouped[k].sort((a, b) =>
        (a.stock_name || "").localeCompare(b.stock_name || "", undefined, {
          sensitivity: "base",
        }),
      );
    });
    return grouped;
  }, [suggestionsInDept]);

  // Auto-fill the amber Toll Processing block from any ticked item that
  // has a `default_processor_id` set on its stock record. First
  // ticked-item with a default wins — you can still override before
  // creating the draft. We only fill when the block is currently empty
  // for the group so manual entries aren't stomped.
  useEffect(() => {
    setTollByGroup((prev) => {
      const next = { ...prev };
      Object.entries(selectedIds).forEach(([sid, sel]) => {
        if (!sel || Object.keys(sel).length === 0) return;
        if (next[sid]?.processor_id || next[sid]?.fee_per_log) return;
        const ticked = (bySupplier[sid] || []).filter((s) => sel[s.stock_id]);
        const withDefault = ticked.find((s) => s.default_processor_id);
        if (withDefault) {
          next[sid] = {
            processor_id: withDefault.default_processor_id,
            fee_per_log:
              withDefault.default_processor_fee_per_log != null
                ? String(withDefault.default_processor_fee_per_log)
                : "",
          };
          setExpandedSuppliers((e) => ({ ...e, [sid]: true }));
        }
      });
      return next;
    });
  }, [selectedIds, bySupplier]);

  const supplierName = (sid) => {
    if (sid === "_unassigned" || !sid) return "Unassigned supplier";
    return suppliers.find((s) => s.id === sid)?.name || sid;
  };

  const toggleSelect = (sid, item, on) => {
    setSelectedIds((s) => {
      const next = { ...s };
      next[sid] = next[sid] || {};
      if (on) {
        next[sid][item.stock_id] = true;
        if (editedQty[item.stock_id] === undefined) {
          setEditedQty((q) => ({ ...q, [item.stock_id]: item.suggested_qty }));
        }
      } else {
        delete next[sid][item.stock_id];
      }
      return next;
    });
  };

  const createDraft = async (sid, { forceNew = false } = {}) => {
    const sel = selectedIds[sid] || {};
    const items = (bySupplier[sid] || [])
      .filter((s) => sel[s.stock_id])
      .map((s) => ({
        stock_id: s.stock_id,
        qty: Number(editedQty[s.stock_id] ?? s.suggested_qty),
        unit_cost: s.unit_cost,
      }));
    if (items.length === 0) return toast.error("Pick at least one item");

    // v182 — If a Draft PO already exists for this supplier, append to it
    // instead of creating a new one. Auto-merges qty on duplicate stock_ids.
    // "sticky note problem" — staff can add items across days and click
    // Send when the bulk order day arrives.
    // v203 — `forceNew` lets a second button bypass this and create a
    // separate PO (urgent-order flow: existing draft is still being built
    // but one item needs to go out ASAP without touching that draft).
    const existingDraft = forceNew
      ? null
      : pos.find(
          (p) =>
            p.status === "draft" &&
            !p.companion_of &&
            ((sid === "_unassigned" && !p.supplier_id) || p.supplier_id === sid),
        );

    setCreatingForSupplier(sid);
    setBusy(true);
    try {
      if (existingDraft) {
        const { data } = await api.post(`/po/${existingDraft.id}/lines`, { items });
        toast.success(`Added to draft ${existingDraft.po_number}`, {
          description: data.companion
            ? `${items.length} line${items.length === 1 ? "" : "s"} · cutting companion updated`
            : `${items.length} line${items.length === 1 ? "" : "s"} appended`,
          duration: 5000,
        });
        setSelectedIds((s) => ({ ...s, [sid]: {} }));
        setHighlightId(existingDraft.id);
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="po-${existingDraft.id}"]`);
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
        setTimeout(() => setHighlightId(null), 3000);
        load();
        return;
      }
      // Attach toll-processing if the manager filled it in on this group.
      // Backend will auto-create a companion PO to the processor.
      const toll = tollByGroup[sid] || {};
      const processor_id = toll.processor_id || null;
      const fee_per_log = Number(toll.fee_per_log || 0) || null;
      const subscription_interval_days =
        Number(subByGroup[sid] || 0) || null;
      const { data } = await api.post("/po", {
        supplier_id: sid === "_unassigned" ? null : sid,
        items,
        processor_id,
        fee_per_log,
        subscription_interval_days,
      });
      if (data.companion_id) {
        toast.success(
          `Draft ${data.po_number} created`,
          {
            description:
              "Linked toll-processing PO also drafted — send both from the list below.",
            duration: 6000,
          },
        );
      } else {
        toast.success(`Draft ${data.po_number} created`);
      }
      setSelectedIds((s) => ({ ...s, [sid]: {} }));
      // Reset the toll block for this group so the next draft starts clean.
      setTollByGroup((t) => ({ ...t, [sid]: {} }));
      setSubByGroup((s) => ({ ...s, [sid]: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save draft");
    } finally {
      setBusy(false);
      setCreatingForSupplier("");
    }
  };

  // Suppliers eligible to be picked as toll-processor for a PO. Filter to
  // the `processor` and `both` roles so ordinary vendors don't clutter the
  // dropdown. Legacy suppliers without a role default to `supplier`.
  const processorOptions = useMemo(
    () =>
      suppliers.filter(
        (s) => s.is_active && (s.role === "processor" || s.role === "both"),
      ),
    [suppliers],
  );

  const deletePo = async (id) => {
    if (!window.confirm("Delete this draft PO?")) return;
    try {
      await api.delete(`/po/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const markReceived = async (id) => {
    try {
      // v178 — "PO Actioned" now writes a distinct `actioned` status so
      // the Receive dashboard can still surface it as "goods in transit"
      // AND downstream reports (Supplier Spend, companion PO folding)
      // count it. Previously we set `received` which visually collided
      // with physical delivery and hid the PO from Receive.
      await api.put(`/po/${id}`, { status: "actioned" });
      toast.success("PO Actioned");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="orders-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="label-eyebrow">Reorder suggestions · draft POs · email send</div>
          <h1 className="font-display text-5xl leading-none mt-2">Orders</h1>
        </div>
      </div>

      {/* v184 — Dashboard mini-nav. Sticky compact strip with counts +
          jump anchors so managers don't scroll a long Suggested list
          just to reach Drafts / Sent. Buttons scroll to the panels below. */}
      {(() => {
        const draftCount = pos.filter((p) => p.status === "draft").length;
        const sentCount = pos.filter((p) => p.status === "sent").length;
        const actionedCount = pos.filter((p) => p.status === "actioned" || p.status === "partially_received").length;
        const jumpToPO = (filter) => {
          setPoStatusFilter(filter);
          setPoListCollapsed(false);
          setTimeout(() => {
            const el = document.querySelector('[data-testid="po-list-panel"]');
            el?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 100);
        };
        const jumpToSuggestions = () => {
          setSuggestionsCollapsed(false);
          setTimeout(() => {
            const el = document.querySelector('[data-testid="suggestions-panel"]');
            el?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 100);
        };
        return (
          <div
            className="mb-6 nb-card p-3 flex items-center gap-2 flex-wrap sticky top-2 z-20 bg-white/90 backdrop-blur"
            data-testid="orders-mini-nav"
          >
            <span className="text-[10px] font-black tracking-wider uppercase text-[#607087]">
              Jump to
            </span>
            <button
              type="button"
              data-testid="orders-jump-suggestions"
              onClick={jumpToSuggestions}
              className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1.5"
            >
              <Sparkles size={12} strokeWidth={2.5} className="text-[#228EBF]" />
              Suggested
              <span className="font-mono-num text-[#607087]">{suggestions.length}</span>
            </button>
            <button
              type="button"
              data-testid="orders-jump-drafts"
              onClick={() => jumpToPO("draft")}
              className={`nb-btn px-3 py-1.5 text-xs flex items-center gap-1.5 ${
                draftCount > 0
                  ? "bg-[#FFF6DF] border-[#9A6F00] text-[#9A6F00]"
                  : "bg-white"
              }`}
            >
              <FileText size={12} strokeWidth={2.5} />
              Drafts
              <span className="font-mono-num">{draftCount}</span>
            </button>
            <button
              type="button"
              data-testid="orders-jump-sent"
              onClick={() => jumpToPO("sent")}
              className={`nb-btn px-3 py-1.5 text-xs flex items-center gap-1.5 ${
                sentCount > 0
                  ? "bg-[#DFEEF7] border-[#0A3A7A] text-[#0A3A7A]"
                  : "bg-white"
              }`}
            >
              <Send size={12} strokeWidth={2.5} />
              Sent
              <span className="font-mono-num">{sentCount}</span>
            </button>
            <button
              type="button"
              data-testid="orders-jump-actioned"
              onClick={() => jumpToPO("active")}
              className={`nb-btn px-3 py-1.5 text-xs flex items-center gap-1.5 ${
                actionedCount > 0
                  ? "bg-[#E6E1F5] border-[#4B2E9B] text-[#4B2E9B]"
                  : "bg-white"
              }`}
            >
              <CheckCircle2 size={12} strokeWidth={2.5} />
              Actioned
              <span className="font-mono-num">{actionedCount}</span>
            </button>
          </div>
        );
      })()}

      {/* SUGGESTIONS */}
      <div className="nb-card p-5 mb-6" data-testid="suggestions-panel">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Sparkles size={20} strokeWidth={2.5} className="text-[#228EBF]" />
          <h2 className="font-display text-xl">Suggested reorders</h2>
          {suggestionsInDept.length > 0 && (
            <span className="text-xs text-[#607087]">
              · {suggestionsInDept.length} item{suggestionsInDept.length === 1 ? "" : "s"}
              {deptFilter !== "all" && suggestions.length !== suggestionsInDept.length && (
                <span> of {suggestions.length}</span>
              )}
            </span>
          )}
          {suggestions.length > 0 && (
            <div className="ml-auto flex items-center gap-2">
              {!suggestionsCollapsed && (
                <>
                  <button
                    data-testid="orders-expand-all"
                    onClick={() => {
                      const all = {};
                      Object.keys(bySupplier).forEach((sid) => (all[sid] = true));
                      setExpandedSuppliers(all);
                    }}
                    className="nb-btn bg-white px-3 py-1 text-xs font-bold"
                  >
                    Expand all
                  </button>
                  <button
                    data-testid="orders-collapse-all"
                    onClick={() => setExpandedSuppliers({})}
                    className="nb-btn bg-white px-3 py-1 text-xs font-bold"
                  >
                    Collapse all
                  </button>
                </>
              )}
              <button
                data-testid="orders-suggestions-toggle"
                onClick={() => setSuggestionsCollapsed((v) => !v)}
                className="nb-btn bg-white px-3 py-1 text-xs font-bold flex items-center gap-1"
                title={suggestionsCollapsed ? "Show Suggested reorders" : "Hide Suggested reorders"}
              >
                {suggestionsCollapsed ? (
                  <>
                    <ChevronDown size={12} strokeWidth={2.5} /> Show
                  </>
                ) : (
                  <>
                    <ChevronUp size={12} strokeWidth={2.5} /> Hide
                  </>
                )}
              </button>
            </div>
          )}
        </div>
        {/* v195 — Department filter chips. Filters both this panel and the
            PO list below in one control. Staff default to their own dept. */}
        {!suggestionsCollapsed && (
          <div className="mb-3 flex items-center gap-2 flex-wrap">
            <span className="label-eyebrow shrink-0">Filter by dept</span>
            <DepartmentChips
              value={deptFilter}
              onChange={setDeptFilter}
              testidPrefix="orders-dept"
            />
          </div>
        )}
        {suggestionsCollapsed ? null : suggestionsInDept.length === 0 ? (
          <div className="text-sm text-[#607087] py-6 text-center">
            {suggestions.length === 0 ? (
              <>
                Nothing to reorder right now — all items are above their threshold,
                <br />
                <span className="text-[11px]">
                  or already on an open draft / sent PO below.
                </span>
              </>
            ) : (
              <>
                No suppliers primarily-owned by <b>{deptFilter}</b> have low stock.
                <br />
                <span className="text-[11px]">
                  Tap <b>All depts</b> to see {suggestions.length} item
                  {suggestions.length === 1 ? "" : "s"} across other suppliers,
                  or set a Primary Owner on the Suppliers page.
                </span>
              </>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {Object.entries(bySupplier).map(([sid, list]) => {
              const expanded = !!expandedSuppliers[sid];
              const selCountAll = list.filter(
                (s) => (selectedIds[sid] || {})[s.stock_id],
              ).length;
              return (
              <div
                key={sid}
                className="border-2 border-black rounded-xl p-4"
                data-testid={`supplier-group-${sid}`}
              >
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      data-testid={`supplier-toggle-${sid}`}
                      onClick={() =>
                        setExpandedSuppliers((s) => ({
                          ...s,
                          [sid]: !s[sid],
                        }))
                      }
                      className="nb-btn bg-white p-1.5"
                      aria-label={expanded ? "Collapse" : "Expand"}
                      aria-expanded={expanded}
                    >
                      {expanded ? (
                        <ChevronDown size={16} strokeWidth={2.5} />
                      ) : (
                        <ChevronRight size={16} strokeWidth={2.5} />
                      )}
                    </button>
                    <h3
                      className="font-display text-lg cursor-pointer"
                      onClick={() =>
                        setExpandedSuppliers((s) => ({
                          ...s,
                          [sid]: !s[sid],
                        }))
                      }
                    >
                      {supplierName(sid)}
                    </h3>
                    <span
                      className="text-[11px] font-bold text-[#607087]"
                      data-testid={`supplier-count-${sid}`}
                    >
                      {list.length} item{list.length === 1 ? "" : "s"}
                      {selCountAll > 0 && ` · ${selCountAll} picked`}
                    </span>
                    {(() => {
                      const sel = selectedIds[sid] || {};
                      const selCount = list.filter((s) => sel[s.stock_id]).length;
                      const allOn = selCount === list.length && list.length > 0;
                      const toggleAll = () => {
                        setSelectedIds((s) => {
                          const next = { ...s };
                          if (allOn) {
                            next[sid] = {};
                          } else {
                            next[sid] = { ...(next[sid] || {}) };
                            list.forEach((it) => {
                              next[sid][it.stock_id] = true;
                            });
                            // Prime edited qty for newly-selected items
                            setEditedQty((q) => {
                              const nq = { ...q };
                              list.forEach((it) => {
                                if (nq[it.stock_id] === undefined) {
                                  nq[it.stock_id] = it.suggested_qty;
                                }
                              });
                              return nq;
                            });
                          }
                          return next;
                        });
                      };
                      return (
                        <button
                          data-testid={`select-all-${sid}`}
                          onClick={toggleAll}
                          className="nb-btn bg-white px-3 py-1 text-xs flex items-center gap-1.5"
                          title={
                            allOn
                              ? "Deselect all items from this supplier"
                              : "Select all items from this supplier"
                          }
                        >
                          <input
                            type="checkbox"
                            readOnly
                            checked={allOn}
                            className="w-3.5 h-3.5 pointer-events-none"
                            data-testid={`select-all-checkbox-${sid}`}
                          />
                          {allOn ? "Deselect all" : "Select all"}
                          <span className="label-eyebrow ml-1 text-[#607087]">
                            ({selCount}/{list.length})
                          </span>
                        </button>
                      );
                    })()}
                  </div>
                  {(() => {
                    // v182 — If a draft already exists for this supplier,
                    // the primary CTA becomes "Add to Draft PO-…" (blue).
                    // v203 — When a draft already exists, show a SECOND
                    // button "Create new PO" (danger-ish amber) so an
                    // urgent-order flow can leave the staged draft alone.
                    const existingDraft = pos.find(
                      (p) =>
                        p.status === "draft" &&
                        !p.companion_of &&
                        ((sid === "_unassigned" && !p.supplier_id) || p.supplier_id === sid),
                    );
                    const disabled =
                      busy ||
                      !selectedIds[sid] ||
                      Object.keys(selectedIds[sid]).length === 0;
                    return (
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          data-testid={`create-draft-${sid}`}
                          onClick={() => createDraft(sid)}
                          disabled={disabled}
                          className={`nb-btn px-4 py-2 flex items-center gap-2 text-sm ${
                            existingDraft ? "bg-[#DFEEF7] border-[#0A3A7A] text-[#0A3A7A]" : "nb-btn-primary"
                          }`}
                          title={
                            existingDraft
                              ? `Append to open draft ${existingDraft.po_number} — click Send on the draft when your bulk order day arrives`
                              : "Create a fresh draft PO for this supplier"
                          }
                        >
                          <ShoppingCart size={16} strokeWidth={2.5} />
                          {creatingForSupplier === sid
                            ? "..."
                            : existingDraft
                              ? `Add to ${existingDraft.po_number}`
                              : "Create draft PO"}
                        </button>
                        {existingDraft && (
                          <button
                            data-testid={`create-new-po-${sid}`}
                            onClick={() => createDraft(sid, { forceNew: true })}
                            disabled={disabled}
                            className="nb-btn bg-[#FFF8E1] border-[#7A5300] text-[#7A5300] px-4 py-2 flex items-center gap-2 text-sm font-bold"
                            title={`Create a separate PO without touching draft ${existingDraft.po_number} — use for urgent orders that shouldn't wait for the bulk one`}
                          >
                            <ShoppingCart size={16} strokeWidth={2.5} />
                            Create new PO
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {expanded && sid !== "_unassigned" && processorOptions.length > 0 && (
                  <details
                    className="mb-3 border-2 border-black rounded-lg bg-[#FFF8E1]"
                    data-testid={`toll-processing-${sid}`}
                  >
                    <summary className="cursor-pointer px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                      <span>Toll processing (cut / laminate / finish)</span>
                      {tollByGroup[sid]?.processor_id && (
                        <span className="text-[10px] font-bold bg-[#E5A72E] text-black px-1.5 py-0.5 rounded">
                          {suppliers.find((s) => s.id === tollByGroup[sid].processor_id)?.name}
                        </span>
                      )}
                    </summary>
                    <div className="px-3 pb-3 pt-1 flex flex-col sm:flex-row gap-2">
                      <label className="flex-1">
                        <div className="label-eyebrow mb-1">Processor</div>
                        <select
                          data-testid={`toll-processor-${sid}`}
                          className="nb-input py-1.5 text-sm"
                          value={tollByGroup[sid]?.processor_id || ""}
                          onChange={(e) =>
                            setTollByGroup((t) => ({
                              ...t,
                              [sid]: { ...(t[sid] || {}), processor_id: e.target.value },
                            }))
                          }
                        >
                          <option value="">— none —</option>
                          {processorOptions.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="w-full sm:w-40">
                        <div className="label-eyebrow mb-1">Fee per log ($)</div>
                        <input
                          data-testid={`toll-fee-${sid}`}
                          type="number"
                          step="0.01"
                          min="0"
                          className="nb-input py-1.5 text-sm font-mono-num"
                          value={tollByGroup[sid]?.fee_per_log || ""}
                          onChange={(e) =>
                            setTollByGroup((t) => ({
                              ...t,
                              [sid]: { ...(t[sid] || {}), fee_per_log: e.target.value },
                            }))
                          }
                          placeholder="e.g. 25.00"
                        />
                      </label>
                    </div>
                    <div className="px-3 pb-3 text-[11px] text-[#7A5300]">
                      Auto-creates a linked companion PO to the processor · 10
                      wheels per log · billed separately.
                    </div>
                  </details>
                )}
                {expanded && sid !== "_unassigned" && (
                  <details
                    className="mb-3 border-2 border-black rounded-lg bg-[#EAF5FB]"
                    data-testid={`subscription-panel-${sid}`}
                  >
                    <summary className="cursor-pointer px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                      <span>Recurring subscription (e.g. every 2 weeks)</span>
                      {subByGroup[sid] && (
                        <span className="text-[10px] font-bold bg-[#228EBF] text-white px-1.5 py-0.5 rounded">
                          Every {{ 7: "week", 14: "2 weeks", 30: "month", 90: "quarter" }[subByGroup[sid]] || `${subByGroup[sid]} days`}
                        </span>
                      )}
                    </summary>
                    <div className="px-3 pb-3 pt-1 flex flex-col sm:flex-row gap-2 items-end">
                      <label className="flex-1">
                        <div className="label-eyebrow mb-1">Cycle</div>
                        <select
                          data-testid={`sub-interval-${sid}`}
                          className="nb-input py-1.5 text-sm"
                          value={subByGroup[sid] || ""}
                          onChange={(e) =>
                            setSubByGroup((s) => ({ ...s, [sid]: e.target.value }))
                          }
                        >
                          <option value="">— one-off (not recurring) —</option>
                          <option value="7">Weekly</option>
                          <option value="14">Fortnightly (every 2 weeks)</option>
                          <option value="30">Monthly</option>
                          <option value="90">Quarterly</option>
                        </select>
                      </label>
                    </div>
                    <div className="px-3 pb-3 text-[11px] text-[#0A3A7A]">
                      Once received, a fresh actioned PO auto-appears in
                      <b> Receive</b> for the next cycle — no re-email to the
                      supplier. Qty per delivery can vary (spring water / milk):
                      just type the actual delivered qty at receive time.
                    </div>
                  </details>
                )}
                {expanded && (
                  <div className="flex flex-col gap-2">
                    {list.map((it) => {
                      const checked = !!(selectedIds[sid] || {})[it.stock_id];
                      const qty = editedQty[it.stock_id] ?? it.suggested_qty;
                      return (
                        <div
                          key={it.stock_id}
                          className={`flex items-center gap-3 border-2 border-black rounded-lg p-2 transition-all ${
                            highlightId === it.stock_id
                              ? "ring-4 ring-[#228EBF] shadow-lg bg-[#EAF5FB]"
                              : ""
                          }`}
                          data-testid={`suggest-${it.stock_id}`}
                        >
                          <input
                            type="checkbox"
                            data-testid={`suggest-pick-${it.stock_id}`}
                            checked={checked}
                            onChange={(e) =>
                              toggleSelect(sid, it, e.target.checked)
                            }
                            className="w-5 h-5"
                          />
                          <StockThumb item={it} size="h-12 w-12" />
                          <div className="flex-1 min-w-0">
                            <div className="font-display text-base truncate">
                              {it.stock_name}
                            </div>
                            <div className="label-eyebrow">
                              now {fmtQty(it.current_qty)} {it.unit} · used{" "}
                              {fmtQty(it.weekly_usage)}/wk
                            </div>
                            {it.order_contact_name && (
                              <div
                                className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold tracking-wider uppercase text-[#1F3B6B] bg-[#DFEEF7] border border-[#001740] rounded px-1.5 py-0.5"
                                data-testid={`suggest-order-contact-${it.stock_id}`}
                                title="Team member usually forwarded this reorder"
                              >
                                Orders: {it.order_contact_name}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              data-testid={`qty-minus-${it.stock_id}`}
                              onClick={() =>
                                setEditedQty((q) => ({
                                  ...q,
                                  [it.stock_id]: Math.max(
                                    0,
                                    Number(qty) - 1,
                                  ),
                                }))
                              }
                              className="nb-btn bg-white p-2"
                            >
                              <Minus size={14} strokeWidth={2.5} />
                            </button>
                            <input
                              data-testid={`suggest-qty-${it.stock_id}`}
                              type="number"
                              step="0.1"
                              value={qty}
                              onChange={(e) =>
                                setEditedQty((q) => ({
                                  ...q,
                                  [it.stock_id]: e.target.value,
                                }))
                              }
                              className="nb-input font-mono-num w-20 text-center py-1"
                            />
                            <button
                              data-testid={`qty-plus-${it.stock_id}`}
                              onClick={() =>
                                setEditedQty((q) => ({
                                  ...q,
                                  [it.stock_id]: Number(qty) + 1,
                                }))
                              }
                              className="nb-btn bg-white p-2"
                            >
                              <Plus size={14} strokeWidth={2.5} />
                            </button>
                            <span className="label-eyebrow">{it.unit}</span>
                          </div>
                          <div className="font-mono-num text-sm text-[#607087] w-20 text-right">
                            ${(it.unit_cost * Number(qty || 0)).toFixed(0)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* PO LIST */}
      <div className="nb-card p-5" data-testid="po-list-panel">
        {(() => {
          // v181 — count POs per status for the chip labels.
          const counts = pos.reduce((acc, p) => {
            const key = p.status || "draft";
            acc[key] = (acc[key] || 0) + 1;
            acc.all = (acc.all || 0) + 1;
            // "active" bucket = anything still in-flight
            if (!["closed", "cancelled", "received"].includes(key) ||
                (key === "received" && (p.items || []).some((it) => Number(it.qty_received || 0) > 0))) {
              acc.active = (acc.active || 0) + 1;
            }
            return acc;
          }, {});
          const filterChips = [
            { key: "active", label: "Active" },
            { key: "all", label: "All" },
            { key: "draft", label: "Draft" },
            { key: "sent", label: "Sent" },
            { key: "actioned", label: "Actioned" },
            { key: "partially_received", label: "Part received" },
            { key: "received", label: "Delivered" },
            { key: "closed", label: "Closed" },
            { key: "cancelled", label: "Cancelled" },
          ];
          return (
            <div className="flex flex-col gap-3 mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <FileText size={20} strokeWidth={2.5} />
                <h2 className="font-display text-xl">Purchase orders</h2>
                <span className="text-xs text-[#607087]">
                  · {counts.all || 0} total
                </span>
                <div className="ml-auto flex items-center gap-1">
                  <a
                    href="/staff-guide"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="po-staff-guide-link"
                    className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1"
                    title="Open the printable Staff Guide for the PO / Receive cycle"
                  >
                    <Printer size={12} strokeWidth={2.5} /> Staff guide
                  </a>
                  <button
                    type="button"
                    data-testid="po-list-collapse"
                    onClick={() => setPoListCollapsed((v) => !v)}
                    className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1"
                    title={poListCollapsed ? "Show PO list" : "Hide PO list"}
                  >
                    {poListCollapsed ? (
                      <>
                        <ChevronDown size={12} strokeWidth={2.5} /> Show
                      </>
                    ) : (
                      <>
                        <ChevronUp size={12} strokeWidth={2.5} /> Hide
                      </>
                    )}
                  </button>
                </div>
              </div>
              {!poListCollapsed && pos.length > 0 && (
                <>
                  <div className="flex items-center gap-2 flex-wrap" data-testid="po-status-chips">
                    {filterChips.map((c) => {
                      const active = poStatusFilter === c.key;
                      const n = counts[c.key] || 0;
                      if (n === 0 && c.key !== "active" && c.key !== "all") return null;
                      return (
                        <button
                          key={c.key}
                          type="button"
                          data-testid={`po-filter-${c.key}`}
                          onClick={() => setPoStatusFilter(c.key)}
                          className={`text-[11px] font-black tracking-wider uppercase rounded-md border-2 px-2 py-1 transition-colors ${
                            active
                              ? "bg-black text-white border-black"
                              : "bg-white border-black/40 hover:border-black"
                          }`}
                        >
                          {c.label}
                          <span className={`ml-1 font-mono-num ${active ? "opacity-80" : "text-[#607087]"}`}>
                            {n}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="relative">
                    <Search
                      size={14}
                      strokeWidth={2.5}
                      className="absolute left-2.5 top-2.5 text-[#607087]"
                    />
                    <input
                      data-testid="po-search"
                      type="text"
                      value={poSearch}
                      onChange={(e) => setPoSearch(e.target.value)}
                      placeholder="Search PO number, supplier or notes…"
                      className="nb-input pl-8 py-2 text-sm w-full sm:w-96"
                    />
                    {poSearch && (
                      <button
                        type="button"
                        data-testid="po-search-clear"
                        onClick={() => setPoSearch("")}
                        className="absolute right-2 top-2 text-[#607087] hover:text-black"
                      >
                        <X size={14} strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })()}
        {poListCollapsed ? null : posInDept.length === 0 ? (
          <div className="text-sm text-[#607087] py-6 text-center">
            {pos.length === 0
              ? "No POs yet. Create one from the suggestions above."
              : (
                <>
                  No POs owned by <b>{deptFilter}</b>.
                  <br />
                  <span className="text-[11px]">
                    Tap <b>All depts</b> above to see the {pos.length} PO
                    {pos.length === 1 ? "" : "s"} across other suppliers,
                    or set a Primary Owner on the Suppliers page.
                  </span>
                </>
              )}
          </div>
        ) : (() => {
          // v181 — apply status + search filters.
          const q = poSearch.trim().toLowerCase();
          const filtered = posInDept.filter((p) => {
            if (poStatusFilter === "all") {
              // pass
            } else if (poStatusFilter === "active") {
              const terminal = ["closed", "cancelled"];
              if (terminal.includes(p.status)) return false;
              // Fully-delivered (received with real qty_received) is also terminal
              if (
                p.status === "received" &&
                !(p.items || []).some((it) => Number(it.qty_received || 0) > 0) === false
              ) {
                // a real Delivered — hide from Active
                return false;
              }
            } else if (poStatusFilter !== p.status) {
              return false;
            }
            if (!q) return true;
            const hay = [
              p.po_number,
              p.supplier_snapshot?.name,
              p.notes,
              ...(p.items || []).map((i) => i.stock_name),
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();
            return hay.includes(q);
          });
          if (filtered.length === 0) {
            return (
              <div className="text-sm text-[#607087] py-6 text-center" data-testid="po-list-empty">
                No POs match this filter. Try <em>Active</em> or clear the search.
              </div>
            );
          }
          return (
          <div className="flex flex-col gap-2">
            {filtered.map((po) => {
              const st = resolveStatusStyle(po);
              // v184 — Draft-aging chip. Nothing should sit in Draft
              // forever — highlight anything ≥ 7d, escalate at 30d.
              let agingBadge = null;
              if (po.status === "draft" && po.created_at) {
                const days = Math.floor(
                  (Date.now() - new Date(po.created_at)) / 86400000,
                );
                if (days >= 30) {
                  agingBadge = {
                    label: `Aging ${days}d`,
                    bg: "#FFE4E4",
                    border: "#B0272D",
                    fg: "#B0272D",
                    title:
                      "This draft is over a month old — decide whether to Send or Cancel.",
                  };
                } else if (days >= 7) {
                  agingBadge = {
                    label: `Aging ${days}d`,
                    bg: "#FFF3D4",
                    border: "#7A5A00",
                    fg: "#3B2A00",
                    title:
                      "Draft has been sitting for a week — add any last items and Send when ready.",
                  };
                }
              }
              return (
                <div
                  key={po.id}
                  className={`border-2 border-black rounded-xl p-3 flex flex-wrap items-center gap-y-2 gap-x-3 transition-all ${
                    highlightId === po.id ? "ring-4 ring-[#228EBF] shadow-lg bg-[#EAF5FB]" : ""
                  }`}
                  data-testid={`po-${po.id}`}
                >
                  <div
                    className="font-display text-xs px-2 py-0.5 border-2 border-black rounded shrink-0"
                    style={{ background: st.bg, color: st.color }}
                  >
                    {st.label}
                  </div>
                  {agingBadge && (
                    <span
                      className="text-[10px] font-black tracking-wider uppercase rounded px-1.5 py-0.5 border-2 shrink-0"
                      style={{
                        background: agingBadge.bg,
                        borderColor: agingBadge.border,
                        color: agingBadge.fg,
                      }}
                      title={agingBadge.title}
                      data-testid={`po-aging-${po.id}`}
                    >
                      {agingBadge.label}
                    </span>
                  )}
                  <div className="flex-1 min-w-0 basis-full sm:basis-0 order-3 sm:order-2">
                    <div className="font-display text-base flex items-center gap-1.5 flex-wrap">
                      {po.po_number}
                      {po.companion_of && (
                        <span
                          className="text-[10px] font-black tracking-wider uppercase bg-[#FFE9C2] border-2 border-[#7A5300] text-[#7A5300] rounded px-1.5 py-0.5"
                          data-testid={`po-service-badge-${po.id}`}
                          title="Not a purchase — a service request. Send manually or forward to your supplier."
                        >
                          Service request
                        </span>
                      )}
                      {po.subscription_interval_days && (
                        <span
                          className={`text-[10px] font-black tracking-wider uppercase border-2 rounded px-1.5 py-0.5 ${
                            po.subscription_active
                              ? "bg-[#DFEEF7] border-[#0A3A7A] text-[#0A3A7A]"
                              : "bg-[#E4E7EB] border-[#3D4A5C] text-[#3D4A5C]"
                          }`}
                          data-testid={`po-subscription-badge-${po.id}`}
                          title={
                            po.subscription_active
                              ? po.subscription_paused_until
                                ? `Next cycle skipped — resumes after ${po.subscription_paused_until}`
                                : `Auto-clones on receive · every ${po.subscription_interval_days} days`
                              : "Previous cycle in a subscription chain"
                          }
                        >
                          Subscription
                        </span>
                      )}
                      {(po.companion_id || po.companion_of) && (() => {
                        const linkedId = po.companion_id || po.companion_of;
                        const linked = pos.find((x) => x.id === linkedId);
                        const isChild = !!po.companion_of;
                        return (
                          <button
                            type="button"
                            data-testid={`po-companion-link-${po.id}`}
                            onClick={() => {
                              const el = document.querySelector(
                                `[data-testid="po-${linkedId}"]`,
                              );
                              el?.scrollIntoView({ behavior: "smooth", block: "center" });
                              setHighlightId(linkedId);
                              setTimeout(() => setHighlightId(null), 2400);
                            }}
                            className="text-[10px] font-bold tracking-wider uppercase bg-[#FFF8E1] border-2 border-[#E5A72E] rounded px-1.5 py-0.5 hover:bg-[#FFE9C2] text-[#7A5300]"
                            title={isChild ? "Parent order that generated this cutting PO" : "Linked cutting / toll-processing PO"}
                          >
                            {isChild ? "Parent" : "Cutting"} · {linked?.po_number || "—"}
                          </button>
                        );
                      })()}
                    </div>
                    <div className="label-eyebrow truncate">
                      {po.supplier_snapshot?.name || "No supplier"} · {po.items.length}{" "}
                      line{po.items.length !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div className="font-mono-num font-bold ml-auto sm:ml-0 order-2 sm:order-3 shrink-0">
                    ${po.total.toFixed(2)} {po.currency}
                  </div>
                  <div className="flex gap-1 order-4 basis-full sm:basis-auto justify-end">
                    {/* View — available on every PO regardless of status so
                        managers can inspect sent / received orders after the
                        fact. Read-only dialog. */}
                    <button
                      data-testid={`po-view-${po.id}`}
                      onClick={() => setViewPoId(po.id)}
                      className="nb-btn bg-white px-2 py-2 text-xs"
                      aria-label="view PO"
                      title="Open the PO to see line items, notes and totals"
                    >
                      <Eye size={12} strokeWidth={2.5} />
                    </button>
                    {po.status === "draft" && (
                      <>
                        {/* Retrofit "Attach processor" — only shown on drafts
                            that don't already have a companion and aren't
                            themselves a companion. Belt-and-braces guard for
                            managers who missed the amber Toll Processing block
                            when raising the draft (Marlene's Vinyl case). */}
                        {!po.companion_id && !po.companion_of && processorOptions.length > 0 && (
                          <button
                            data-testid={`po-attach-processor-${po.id}`}
                            onClick={() => setAttachProcessorFor(po)}
                            className="nb-btn bg-[#FFF8E1] border-[#E5A72E] text-[#7A5300] px-3 py-2 text-xs flex items-center gap-1"
                            title="Attach a toll-processing partner (e.g. JV Strappings) — creates a linked service-request PO"
                          >
                            <Wrench size={12} strokeWidth={2.5} /> Attach processor
                          </button>
                        )}
                        <button
                          data-testid={`po-send-${po.id}`}
                          onClick={() => setSendFor(po)}
                          className="nb-btn nb-btn-primary px-3 py-2 text-xs flex items-center gap-1"
                        >
                          <Send size={12} strokeWidth={2.5} /> Send
                        </button>
                        <button
                          data-testid={`po-delete-${po.id}`}
                          onClick={() => deletePo(po.id)}
                          className="nb-btn nb-btn-danger px-2 py-2 text-xs"
                          aria-label="delete"
                        >
                          <Trash2 size={12} strokeWidth={2.5} />
                        </button>
                      </>
                    )}
                    {po.status !== "draft" && po.status !== "closed" && po.status !== "cancelled" && (
                      <>
                        <button
                          data-testid={`po-resend-${po.id}`}
                          onClick={() => setSendFor(po)}
                          className="nb-btn bg-white px-3 py-2 text-xs flex items-center gap-1"
                          title="Resend this PO — same content, different recipient or notes"
                        >
                          <Send size={12} strokeWidth={2.5} /> Resend
                        </button>
                        {po.status === "sent" && (
                          <button
                            data-testid={`po-receive-${po.id}`}
                            onClick={() => markReceived(po.id)}
                            className="nb-btn bg-white px-3 py-2 text-xs flex items-center gap-1"
                            title="Confirms the Orders manager has read and placed this order. Physical delivery is recorded by the Receiver on the Receive page."
                          >
                            <CheckCircle2 size={12} strokeWidth={2.5} /> PO Actioned
                          </button>
                        )}
                        {/* v181 — Label is "Cancel" (not "Close") because
                            staff kept confusing "Close" with "close this
                            dialog / window". The underlying reason (manually
                            received / supplier cancelled / duplicate / other)
                            is picked inside the dialog. */}
                        <button
                          data-testid={`po-close-${po.id}`}
                          onClick={() => setClosePo(po)}
                          className="nb-btn bg-white px-3 py-2 text-xs flex items-center gap-1 border-[#B0272D] text-[#B0272D]"
                          title="Cancel this PO — supplier cancelled, duplicate, or already received manually. No stock or cost changes."
                        >
                          <XCircle size={12} strokeWidth={2.5} /> Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          );
        })()}
      </div>

      <POSendDialog
        open={!!sendFor}
        po={sendFor}
        onClose={() => setSendFor(null)}
        onSent={load}
      />
      <AttachProcessorDialog
        open={!!attachProcessorFor}
        po={attachProcessorFor}
        processors={processorOptions}
        onClose={() => setAttachProcessorFor(null)}
        onAttached={load}
      />
      <PODetailDialog
        open={!!viewPoId}
        poId={viewPoId}
        onClose={() => setViewPoId(null)}
        onOpenLinked={(id) => setViewPoId(id)}
      />
      <ClosePODialog
        open={!!closePo}
        po={closePo}
        onClose={() => setClosePo(null)}
        onClosed={load}
      />
    </div>
  );
};

export default Orders;
